import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import * as E from '@react-email/components'
import { json, redirect, type ActionFunctionArgs, type MetaFunction, type LoaderFunctionArgs } from '@remix-run/node'
import { Form, useActionData } from '@remix-run/react'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { Container } from '#app/components/layout/container.tsx'
import { Flex } from '#app/components/layout/flex.tsx'
import { VisuallyHidden } from '#app/components/layout/visually-hidden.tsx'
import { Link } from '#app/components/navigation/link.tsx'
import { H1 } from '#app/components/typography/h1.tsx'
import { P } from '#app/components/typography/p.tsx'
import { Span } from '#app/components/typography/span.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { Input } from '#app/old_components/ui/input.tsx'
import { Label } from '#app/old_components/ui/label.tsx'
import { requireAnonymous } from '#app/utils/auth.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { sendEmail } from '#app/utils/email.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { useIsPending } from '#app/utils/misc.tsx'
import { EmailSchema } from '#app/utils/user-validation.ts'
import { prepareVerification } from './verify.server.ts'

const SignupSchema = z.object({
	email: EmailSchema,
})

export const meta: MetaFunction = () => {
	return [{ title: 'Create Account | VerveUI' }]
}

export const loader = async ({ request }: LoaderFunctionArgs) => {
	await requireAnonymous(request)
	return json({})
}

export default function SignupRoute() {
	const lastResult = useActionData<typeof action>()
	const isPending = useIsPending()

	const [form, { email }] = useForm({
		id: 'signup-form',
		constraint: getZodConstraint(SignupSchema),
		lastResult,
		shouldValidate: 'onSubmit',
		shouldRevalidate: 'onInput',
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: SignupSchema })
		},
	})

	return (
		<Flex items="center">
			<Container>
				<main>
					<Flex orientation="vertical" items="center" gap="14" className="py-10">
						<H1 className="text-center">Let's start your journey!</H1>
						<Flex orientation="vertical" gap="8">
							<Form method="POST" {...getFormProps(form)} aria-invalid={form.errors ? true : undefined} aria-describedby={form.errors ? form.errorId : undefined}>
								<HoneypotInputs />
								<div id={form.errorId}>{form.errors}</div>
								<Flex orientation="vertical" gap="10">
									<VisuallyHidden>
										<Label htmlFor={email.id}>Email</Label>
									</VisuallyHidden>
									<Input
										aria-invalid={email.errorId ? true : undefined}
										aria-describedby={email.errorId}
										placeholder="Enter your email"
										{...getInputProps(email, { type: 'email' })}
										autoFocus
										autoComplete="email"
									/>
									{email.errors?.map(e => (
										<P key={e} className="text-danger-foreground">
											{e}{' '}
											{e === 'An account with this email already exists.' ?
												<Span className="text-foreground">
													Did you mean to <Link to="/login">log in</Link>?
												</Span>
											:	''}
										</P>
									))}
									<StatusButton className="w-full" status={isPending ? 'pending' : lastResult?.status ?? 'idle'} type="submit" disabled={isPending}>
										{isPending ? 'Sending email' : 'Confirm email'}
									</StatusButton>
								</Flex>
							</Form>
						</Flex>
					</Flex>
				</main>
			</Container>
		</Flex>
	)
}

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()

	checkHoneypot(formData)

	const submission = parseWithZod(formData, { schema: SignupSchema })
	if (submission.status !== 'success') {
		return json(submission.reply(), { status: submission.status === 'error' ? 400 : 200 })
	}
	const { email } = submission.value
	const existingUser = await prisma.user.findUnique({ where: { email }, select: { id: true } })
	if (existingUser) {
		return submission.reply({ fieldErrors: { email: ['An account with this email already exists.'] } })
	}

	const { verifyUrl, redirectTo, otp } = await prepareVerification({
		period: 10 * 60,
		request,
		type: 'onboarding',
		target: email,
	})
	const response = await sendEmail({
		to: email,
		subject: `Welcome to VerveUI!`,
		react: <SignupEmail onboardingUrl={verifyUrl.toString()} otp={otp} />,
	})
	if (response.status !== 'success') {
		return submission.reply({ formErrors: ['Email sending failed. Please try again.'] })
	}
	return redirect(redirectTo.toString())
}

export function SignupEmail({ onboardingUrl, otp }: { onboardingUrl: string; otp: string }) {
	return (
		<E.Html lang="en" dir="ltr">
			<E.Container>
				<h1>
					<E.Text>Welcome to VerveUI!</E.Text>
				</h1>
				<p>
					<E.Text>
						Here's your verification code: <strong>{otp}</strong>
					</E.Text>
				</p>
				<p>
					<E.Text>Or click the link to get started:</E.Text>
				</p>
				<E.Link href={onboardingUrl}>{onboardingUrl}</E.Link>
			</E.Container>
		</E.Html>
	)
}
